import {
    BUSINESS_EVENTBRITE_CATEGORY,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
    HEALTH_EVENTBRITE_CATEGORY,
    HOBBIES_EVENTBRITE_CATEGORY,
    MUSIC_EVENTBRITE_CATEGORY,
    NIGHTLIFE_EVENTBRITE_CATEGORY,
    PERFORMING_EVENTBRITE_CATEGORY,
    SEASONAL_EVENTBRITE_CATEGORY,
    SPORTS_EVENTBRITE_CATEGORY,
} from '@eventbrite/categories';
import {
    Arts,
    BuisnessProfession,
    FoodDrink,
    Health,
    Hobbies,
    Mic,
    Sports,
    Tickets,
} from '@eventbrite/eds-iconography';

import { DiscoBall, HolidayCalendar } from '@eventbrite/eds-iconography';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React from 'react';
import { OrganicEventSearchFiltersApiPayload } from '../../../api/searchOrganicEvents';
import SPD_BAR_CRAWL from '../../../static/images/home/seasonal_module_spd_bar_crawls_desktop.webp';
import SPD_BAR_CRAWL_MOBILE from '../../../static/images/home/seasonal_module_spd_bar_crawls_mobile.webp';
import SPD_FESTIVE_FUN from '../../../static/images/home/seasonal_module_spd_festive_fun_desktop.webp';
import SPD_FESTIVE_FUN_MOBILE from '../../../static/images/home/seasonal_module_spd_festive_fun_mobile.webp';
import SPD_PARTIES from '../../../static/images/home/seasonal_module_spd_parties_desktop.webp';
import SPD_PARTIES_MOBILE from '../../../static/images/home/seasonal_module_spd_parties_mobile.webp';
import VALENTINES_CELEBRATE_LOVE from '../../../static/images/home/valentines_celebrate_love.webp';
import VALENTINES_DATE from '../../../static/images/home/valentines_date.webp';
import VALENTINES_GROOVE_ON from '../../../static/images/home/valentines_groove_on.webp';
import VALENTINES_HOBBY from '../../../static/images/home/valentines_hobby.webp';
import { bboxToUrlParam } from '../../../utils/location';
import { buildBBox } from '../utils';
export const WEEKEND_EVENTBRITE_CATEGORY = 'this_weekend';

/****************************** Home Page Category Carousel Constants ********************************/

export const EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP: {
    [key: string]: JSX.Element;
} = {
    [BUSINESS_EVENTBRITE_CATEGORY]: <BuisnessProfession viewBox="0 0 32 33" />,
    [MUSIC_EVENTBRITE_CATEGORY]: <Mic viewBox="0 0 40 41" />,
    [PERFORMING_EVENTBRITE_CATEGORY]: <Arts viewBox="0 0 40 41" />,
    [SEASONAL_EVENTBRITE_CATEGORY]: <HolidayCalendar viewBox="0 0 28 36" />,
    [FOOD_AND_DRINK_EVENTBRITE_CATEGORY]: <FoodDrink viewBox="0 0 40 41" />,
    [WEEKEND_EVENTBRITE_CATEGORY]: <Tickets viewBox="0 0 32 32" />,
    [HEALTH_EVENTBRITE_CATEGORY]: <Health viewBox="0 0 32 32" />,
    [HOBBIES_EVENTBRITE_CATEGORY]: <Hobbies viewBox="0 0 32 32" />,
    [SPORTS_EVENTBRITE_CATEGORY]: <Sports viewBox="0 0 32 32" />,
    [NIGHTLIFE_EVENTBRITE_CATEGORY]: <DiscoBall viewBox="0 0 47 48" />,
};

export const EVENTBRITE_CAROUSEL_CATEGORY_MAPPING = {
    [BUSINESS_EVENTBRITE_CATEGORY]: gettext('Business & Professional events'),
    [MUSIC_EVENTBRITE_CATEGORY]: gettext('Music events'),
    [PERFORMING_EVENTBRITE_CATEGORY]: gettext(
        'Visual & performing arts events',
    ),
    [FOOD_AND_DRINK_EVENTBRITE_CATEGORY]: gettext('Food & drinks events'),
    [WEEKEND_EVENTBRITE_CATEGORY]: gettext('Things to do this weekend 😎'),
};

const VIEW_MORE = gettext('View More');

/****************************** Home Page Category Carousel Constants ********************************/
export interface CATEGORY_CAROUSEL {
    key: string;
    name: GenericLazyString;
    icon: any;
    viewMore: GenericLazyString;
    eventSearch: OrganicEventSearchFiltersApiPayload;
}

export const HOME_PAGE_CATEGORY_CAROUSEL: CATEGORY_CAROUSEL[] = [
    {
        key: WEEKEND_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[WEEKEND_EVENTBRITE_CATEGORY],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[
            WEEKEND_EVENTBRITE_CATEGORY
        ],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            dates: ['current_future', 'this_weekend'],
            aggs: {},
        },
    },
    {
        key: MUSIC_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[MUSIC_EVENTBRITE_CATEGORY],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[MUSIC_EVENTBRITE_CATEGORY],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            tags: [MUSIC_EVENTBRITE_CATEGORY],
            aggs: {},
        },
    },
    {
        key: FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[
            FOOD_AND_DRINK_EVENTBRITE_CATEGORY
        ],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[
            FOOD_AND_DRINK_EVENTBRITE_CATEGORY
        ],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            tags: [FOOD_AND_DRINK_EVENTBRITE_CATEGORY],
            aggs: {},
        },
    },
    {
        key: BUSINESS_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[
            BUSINESS_EVENTBRITE_CATEGORY
        ],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[
            BUSINESS_EVENTBRITE_CATEGORY
        ],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            tags: [BUSINESS_EVENTBRITE_CATEGORY],
            aggs: {},
        },
    },
    {
        key: PERFORMING_EVENTBRITE_CATEGORY,
        name: EVENTBRITE_CAROUSEL_CATEGORY_MAPPING[
            PERFORMING_EVENTBRITE_CATEGORY
        ],
        icon: EVENTBRITE_CATEGORY_CAROUSEL_ICON_MAP[
            PERFORMING_EVENTBRITE_CATEGORY
        ],
        viewMore: VIEW_MORE,
        eventSearch: {
            page_size: 6,
            tags: [PERFORMING_EVENTBRITE_CATEGORY],
            aggs: {},
        },
    },
];

/****************************** Home Page Category Browse CTA Constants ********************************/

export const CATEGORY_BROWSE_KEYS = [
    MUSIC_EVENTBRITE_CATEGORY,
    NIGHTLIFE_EVENTBRITE_CATEGORY,
    PERFORMING_EVENTBRITE_CATEGORY,
    SEASONAL_EVENTBRITE_CATEGORY,
    HEALTH_EVENTBRITE_CATEGORY,
    HOBBIES_EVENTBRITE_CATEGORY,
    BUSINESS_EVENTBRITE_CATEGORY,
    FOOD_AND_DRINK_EVENTBRITE_CATEGORY,
];

interface seasonalModulelData {
    id: number;
    title: string;
    imgSrc: string;
    imgSrcMobile: string;
    bucketUrl: string;
    description: string;
}

export const SEASONAL_MODULE_DATA: { [key: string]: seasonalModulelData[] } = {
    VALENTINE: [
        {
            id: 1,
            title: 'Celebrate Love',
            imgSrc: VALENTINES_CELEBRATE_LOVE,
            imgSrcMobile: '',
            bucketUrl: 'celebrate-love',
            description:
                'Explore events filled with love and joy. Join us in celebrating special moments!',
        },
        {
            id: 2,
            title: 'Groove to the beat',
            imgSrc: VALENTINES_GROOVE_ON,
            imgSrcMobile: '',
            bucketUrl: 'groove-to-the-beat',
            description:
                'Get ready to dance and enjoy music that will make your heart beat faster!',
        },
        {
            id: 3,
            title: 'Love-filled eats',
            imgSrc: VALENTINES_DATE,
            imgSrcMobile: '',
            bucketUrl: 'love-filled-eats',
            description:
                'Indulge in delicious treats and explore a world of culinary delights filled with love.',
        },
        {
            id: 4,
            title: 'Heartfelt hobbies',
            imgSrc: VALENTINES_HOBBY,
            imgSrcMobile: '',
            bucketUrl: 'heartfelt-hobbies',
            description:
                'Discover hobbies that warm your heart. Join us for activities that bring joy!',
        },
    ],
    SPD: [
        {
            id: 1,
            title: 'Bar Crawls',
            imgSrc: SPD_BAR_CRAWL,
            imgSrcMobile: SPD_BAR_CRAWL_MOBILE,
            bucketUrl: 'bar-crawls',
            description: '',
        },
        {
            id: 2,
            title: 'Parties',
            imgSrc: SPD_PARTIES,
            imgSrcMobile: SPD_PARTIES_MOBILE,
            bucketUrl: 'parties',
            description: '',
        },
        {
            id: 3,
            title: 'Festive Fun',
            imgSrc: SPD_FESTIVE_FUN,
            imgSrcMobile: SPD_FESTIVE_FUN_MOBILE,
            bucketUrl: 'festive-fun',
            description: '',
        },
    ],
};

interface seasonalCarouselData {
    title: string | GenericLazyString;
    titleCSS: string;
    heapId: string;
    affCode: string;
    queryParams: any;
}

export const SEASONAL_CAROUSEL_DATA: { [key: string]: seasonalCarouselData } = {
    'Mardi Gras': {
        title: 'Mardi gras events 🎉',
        titleCSS: '',
        heapId: 'homepage-mardi-gras-carousel',
        affCode: 'ehometext',
        queryParams: {
            q: 'mardi',
            price: 'paid',
            page: 1,
            page_size: 40,
            aggs: {},
            dates: ['current_future'],
        },
    },
    SXSW: {
        title: "The best downtown events you don't need a badge for",
        titleCSS: 'wrap-title',
        heapId: 'homepage-sxsw-carousel',
        affCode: 'ehomefeatured',
        queryParams: {
            price: 'paid',
            page: 1,
            page_size: 40,
            aggs: {},
            dates: ['current_future'],
            bbox: bboxToUrlParam(
                buildBBox({ latitude: 30.271, longitude: -97.743 }),
            ),
        },
    },
};
